import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { roundToDecimalPlaces } from '@semios/app-platform-common'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import type { TIrrigationDeviceStatus, TValuesCurrentPoints, TValuesCurrentProperties } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { isEmpty, isNil } from 'lodash'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { smallStore } from 'stores/smallStore/smallStore'
import { apiFetch } from 'utils/apiFetch'
import { withoutPrePostFilterEmitterTypes } from 'utils/isPrePostFilterEmitterType'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { TIrrigationActivityForPointOrZone } from '../../caches/StationValuesCache/StationValuesCache'
import type { TGetCacheUpdatesFromResponseParameters, TGetCacheUpdatesFromResponseReturn } from './_types'
import { createValuesMap } from './_utils/createValuesMap'
import { getAnyEmitterStatus } from './_utils/getAnyEmitterStatus'
import { getHeatmapCSS } from './_utils/getHeatmapCSS'
import { getIrrigationStatusReturn } from './_utils/getIrrigationStatusReturn'
import { getValueType } from './_utils/getValueType'
import { insertEmitterType } from './_utils/insertEmitterType'
import { insertEmitterTypeForStatus } from './_utils/insertEmitterTypeForStatus'
import {
  MILLISECONDS_IN_HOUR,
  STATUS_VALUE_TYPE,
  Z_INDEX_HAS_DATA,
  Z_INDEX_NO_DATA,
  Z_INDEX_NOT_APPLICABLE,
  Z_INDEX_OFF,
} from './_utils/irrigationActivityConstants'
import { isPressureValueType } from './_utils/isPressureValueType'
import { setHeatmapExtremesFromArrayOfValues } from './_utils/setHeatmapExtremesFromArrayOfValues'
import { getResponseAndShapeForCurrentPressure } from './irrigationActivityCurrentPressure'

const makeApiArgs = (
  processedCaches: TGetCacheUpdatesFromResponseParameters['processedCaches'],
): routes.ValuesCurrent.Request => {
  const { selectedEmitters } = mapControlsStore.getState()
  const pointValueType = getValueType(MAP_VISUAL.POINT, 'irrigation_activity_pressure')
  const propertyValueType = getValueType(MAP_VISUAL.PROPERTY, 'irrigation_activity_pressure')
  const returner: routes.ValuesCurrent.Request = {}

  const valueTypes = selectedEmitters.flatMap((emitter) => [
    insertEmitterType(pointValueType, emitter),
    insertEmitterTypeForStatus(emitter),
  ])

  const propertyValueTypes = selectedEmitters.flatMap((emitter) => [
    insertEmitterType(propertyValueType, emitter),
    insertEmitterTypeForStatus(emitter),
  ])

  const propertyItems = processedCaches.propertyValues.itemsWithinView
  const stationItems = processedCaches.stationValues.itemsWithinView

  if (stationItems.length) {
    const pointsToFetch = stationItems.map((station) => station.meta.lngLat)

    returner.points = {
      lngLats: pointsToFetch,
      values: createValuesMap(valueTypes),
    }
  }

  if (propertyItems.length !== 0) {
    returner.properties = {
      propertyIds: propertyItems.map((property) => Number(property.id)),
      values: createValuesMap(propertyValueTypes),
    }
  }

  return returner
}

export const getResponseAndShapeForCacheUpdate = async (
  parameters: TGetCacheUpdatesFromResponseParameters,
): Promise<TGetCacheUpdatesFromResponseReturn> => {
  if (isPressureValueType(getValueType(MAP_VISUAL.POINT, 'irrigation_activity_pressure')))
    return getResponseAndShapeForCurrentPressure(parameters)

  const { cacheKeys, processedCaches } = parameters
  const args = makeApiArgs(processedCaches)

  if (isEmpty(args)) return {}

  const response = await apiFetch({ url: '/values-current', body: args })
  const pointValueType = getValueType(MAP_VISUAL.POINT, 'irrigation_activity_pressure')
  const propertyValueType = getValueType(MAP_VISUAL.PROPERTY, 'irrigation_activity_pressure')
  const { selectedEmitters } = mapControlsStore.getState()
  const propertyItems = processedCaches.propertyValues.itemsWithinView
  const stationItems = processedCaches.stationValues.itemsWithinView
  const returner: TGetCacheUpdatesFromResponseReturn = {}
  const valueTypeIsForStatus = pointValueType === STATUS_VALUE_TYPE
  const appliedIrrigationActivityDisplay = smallStore.getState().appliedIrrigationActivityDisplay
  const allValuesForHeatmap: number[] = []
  const properties = fieldAssetStore.getState().properties ?? {}

  if (!!stationItems.length) {
    const itemIdsWithinView: string[] = []

    const itemsWithinViewThatNowHaveValues = stationItems.flatMap(({ meta, id }) => {
      const point = properties?.[meta.propertyId]?.points?.[meta.lngLat]
      const responsePoint = response.points?.[meta.lngLat] ?? {}
      const propertyEmitters = properties[Number(meta.propertyId)]?.irrigationZoneEmitters
      const irrigationZoneId = point?.configuration?.irrigationZoneId

      itemIdsWithinView.push(id)

      const irrigationEmittersWithFlowRatesForPoint = withoutPrePostFilterEmitterTypes(
        point?.configuration?.irrigationEmitterTypesAvailable,
      )
        .filter((emitter) => selectedEmitters.includes(emitter))
        .reduce<TIrrigationActivityForPointOrZone>((acc, emitterType) => {
          const idAsValueType = insertEmitterType(pointValueType, emitterType) as TValuesCurrentPoints
          const emitterStatusValueType = insertEmitterTypeForStatus(emitterType) as TValuesCurrentPoints
          const irrigationZoneEmitterId = `${irrigationZoneId}|||${emitterType}` as TIrrigationZoneEmitterId
          const flowRate = propertyEmitters?.[irrigationZoneEmitterId]?.flowRate
          const duration = Number(responsePoint[idAsValueType]?.value)

          const status = (responsePoint[emitterStatusValueType]?.value ??
            'unknown') as TIrrigationDeviceStatus

          const applied =
            duration && flowRate?.mmPerHour ? (duration / MILLISECONDS_IN_HOUR) * flowRate.mmPerHour : null

          const getBadgeColors = () => getHeatmapCSS({ value: applied })

          acc[irrigationZoneEmitterId] = {
            status,
            duration,
            applied,
            getBadgeColors,
            flowRate,
          }

          return acc
        }, {})

      if (valueTypeIsForStatus) {
        // the point's own any-emitter status also counts the pre and post filter sensors, which the card leaves out
        const status = getAnyEmitterStatus(
          Object.values(irrigationEmittersWithFlowRatesForPoint).map((emitter) => emitter.status),
        )

        return {
          id: String(id),
          value: {
            [pointValueType]: {
              ...getIrrigationStatusReturn(status),
              valuesForPoint: irrigationEmittersWithFlowRatesForPoint,
            },
          },
        }
      }

      /**
       * when working with applied amounts of water, we will show it as
       * hours on the map xor volume applied using an hourly flow rate
       */
      if (appliedIrrigationActivityDisplay === 'DURATION') {
        const totalDurationMs = Object.values(irrigationEmittersWithFlowRatesForPoint).reduce(
          (acc, { duration }) => acc + (duration || 0),
          0,
        )

        const durationHours = totalDurationMs / MILLISECONDS_IN_HOUR
        const durationHoursRounded = Number(roundToDecimalPlaces(durationHours, 1))

        allValuesForHeatmap.push(durationHoursRounded)

        return {
          id: String(id),
          value: {
            [pointValueType]: {
              baseZIndex: durationHours > 0 ? Z_INDEX_HAS_DATA : Z_INDEX_OFF,
              children: durationHoursRounded,
              getContainerCSS: () => getHeatmapCSS({ value: durationHours }),
              valuesForPoint: irrigationEmittersWithFlowRatesForPoint,
            },
          },
        }
        // VOLUME
      } else {
        const appliedWaterMM = Object.values(irrigationEmittersWithFlowRatesForPoint).reduce<number>(
          (acc, curr) => {
            return acc + (curr.applied ?? 0)
          },
          0,
        )

        const appliedAmountFinalUnit = unitConverter.irrigationActivity(appliedWaterMM).value()

        if (appliedAmountFinalUnit) allValuesForHeatmap.push(appliedAmountFinalUnit)

        return {
          id: String(id),
          value: {
            [pointValueType]: {
              // nice to make non-zero amounts pop out more
              baseZIndex: appliedWaterMM > 0 ? Z_INDEX_HAS_DATA : Z_INDEX_OFF,
              children: !!appliedWaterMM ? appliedAmountFinalUnit : 0,
              getContainerCSS: () => getHeatmapCSS({ value: appliedAmountFinalUnit }),
              valuesForPoint: irrigationEmittersWithFlowRatesForPoint,
            },
          },
        }
      }
    })

    returner.stations = {
      itemsWithinViewThatNowHaveValues,
      cacheKey: cacheKeys.stationCacheKey,
      itemIdsWithinView,
    }
  }

  const showValuesForPropertyRatherThanName =
    propertyValueType &&
    !stationItems.length &&
    (appliedIrrigationActivityDisplay === 'DURATION' || valueTypeIsForStatus)

  const itemsWithinViewThatNowHaveValues = propertyItems.map((property) => {
    if (!showValuesForPropertyRatherThanName) {
      return {
        id: String(property.id),
        value: {
          [propertyValueType]: {
            baseZIndex: Z_INDEX_NOT_APPLICABLE,
            children: property.meta.propertyName,
            getContainerCSS: () => ({ backgroundColor: colors.midnight, color: colors.white }),
            onHover: false,
          },
        },
      }
    }

    const propertyFromResponse = response.properties?.[property.id] ?? {}

    const irrigationZoneEmitterIds = Object.keys(
      properties[Number(property.id)]?.irrigationZoneEmitters ?? {},
    ) as TIrrigationZoneEmitterId[]

    const matchingZoneEmitterIdsOnProperty = irrigationZoneEmitterIds.filter((z) => {
      const { emitterType } = getIdAndEmitterTypeFromZoneEmitterTypeKey(z)

      return selectedEmitters.includes(emitterType)
    })

    /**
     * if the selected value type is for an applied amount, then it is
     * for a specific emitter type, but if the property doesn't have
     * that emitter type, we should just show "N/A" for the property.
     * We can determine if the emitter is present for the property by
     * checking the property from the fieldAssetStore's properties
     * object
     */
    const propertyPressureTransducers = Object.values(properties?.[Number(property.id)]?.points || {}).filter(
      (p) => !isEmpty(withoutPrePostFilterEmitterTypes(p.configuration.irrigationEmitterTypesAvailable)),
    )

    const propertyHasChosenEmitterAndEmitterIsSelected = selectedEmitters.some((emitter) => {
      return (
        propertyPressureTransducers?.some((p) =>
          withoutPrePostFilterEmitterTypes(p.configuration.irrigationEmitterTypesAvailable).some(
            (availableEmitter) => availableEmitter === emitter,
          ),
        ) && matchingZoneEmitterIdsOnProperty.some((z) => z.includes(emitter))
      )
    })

    if (isEmpty(propertyPressureTransducers) || !propertyHasChosenEmitterAndEmitterIsSelected) {
      return {
        id: String(property.id),
        value: {
          [propertyValueType]: {
            children: translate.phrases.abbreviations('Not Applicable'),
            getContainerCSS: () => ({ backgroundColor: colors.grey200, color: colors.midnight }),
            baseZIndex: Z_INDEX_NOT_APPLICABLE,
            onHover: true,
          },
        },
      }
    }

    const nonStatusValueTypes = Object.keys(propertyFromResponse).filter(
      (key) => !key.startsWith(STATUS_VALUE_TYPE),
    )

    const aggregatedDuration = nonStatusValueTypes.reduce<number>((acc, id) => {
      const propertyValue = propertyFromResponse[id as TValuesCurrentProperties]

      if (propertyValue && 'max_value' in propertyValue) {
        acc += Number(propertyValue.max_value)
      }

      return acc
    }, 0)

    const value = valueTypeIsForStatus ? propertyFromResponse[STATUS_VALUE_TYPE]?.value : aggregatedDuration

    if (!isNil(value)) {
      if (valueTypeIsForStatus) {
        return {
          id: String(property.id),
          value: {
            [propertyValueType]: {
              ...getIrrigationStatusReturn(value as TIrrigationDeviceStatus),
              onHover: true,
            },
          },
        }
      }

      const valueAsNumber = Number(value)
      const durationHours = valueAsNumber / MILLISECONDS_IN_HOUR
      const durationHoursRounded = Number(roundToDecimalPlaces(durationHours, 1))
      // note that we will never show applied amounts at the property level, just duration

      allValuesForHeatmap.push(durationHoursRounded)

      return {
        id: String(property.id),
        value: {
          [propertyValueType]: {
            // nice to make non-zero amounts pop out more
            baseZIndex: durationHours > 0 ? Z_INDEX_HAS_DATA : Z_INDEX_OFF,
            children: durationHoursRounded,
            getContainerCSS: () => getHeatmapCSS({ value: durationHours }),
            onHover: true,
          },
        },
      }
    }

    return {
      id: String(property.id),
      value: {
        [propertyValueType]: {
          children: translate.phrases.templates('-'),
          getContainerCSS: () => ({ backgroundColor: colors.grey200, color: colors.midnight }),
          baseZIndex: Z_INDEX_NO_DATA,
          onHover: true,
        },
      },
    }
  })

  setHeatmapExtremesFromArrayOfValues({
    heatmapColoring: [
      colors.irrigationHeatmapSoftBlue,
      colors.irrigationHeatmapMediumBlue,
      colors.irrigationHeatmapStrongBlue,
    ],
    values: allValuesForHeatmap,
    isStale: processedCaches.isStale,
  })

  returner.properties = {
    itemsWithinViewThatNowHaveValues,
    cacheKey: cacheKeys.propertyCacheKey,
    itemIdsWithinView: processedCaches.propertyValues.itemIdsWithinView,
  }

  return returner
}
